/* eslint-disable max-lines */
import { reportSectionMarkerResidue } from '@/components/tiptap/shared/section-residue';
import { simpleMarkdownToHtmlForPrint } from '@/helpers/markdownToHtml';
import { tutorialVisibleAtom } from '@/hooks/useTutorial';
import { isTauri } from '@/tauri/utils';
import { logger } from '@/utils/logger/logger';
import { DOCUMENT_BASE_CSS, DOCUMENT_FONT_STACK } from '@heidi/session/contracts';
import { getDefaultStore } from 'jotai';

export interface PrintDocumentOptions {
  markdownContent: string;
  /**
   * Pre-rendered HTML to print. When provided it is used as-is, bypassing
   * the `markdownContent` → `simpleMarkdownToHtmlForPrint` conversion. Callers
   * pass this to print copy-settings-transformed HTML (matching Export), so
   * the same four toggles apply. Omitting it preserves the legacy markdown path.
   */
  htmlContent?: string;
  sessionId?: string;
  documentId?: string;
  onPrintStart?: (data: { sessionId: string; documentId?: string }) => void;
}

const createPrintFrame = (htmlContent: string) => {
  const printFrame = document.createElement('iframe');
  printFrame.setAttribute('aria-hidden', 'true');
  printFrame.setAttribute('tabindex', '-1');
  printFrame.style.position = 'fixed';
  printFrame.style.left = '-9999px';
  printFrame.style.top = '0';
  printFrame.style.width = '100vw';
  printFrame.style.height = '100vh';
  printFrame.style.border = '0';
  printFrame.style.opacity = '0';
  printFrame.style.visibility = 'hidden';
  printFrame.style.pointerEvents = 'none';

  printFrame.srcdoc = `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="utf-8"/>
        <style>
          /* Shared with Export so the two outputs can't drift. */
          ${DOCUMENT_BASE_CSS}

          /* Screen inset for the off-screen iframe; @media print overrides
             it with the page margin below. */
          body { margin: 10mm; }

          @media print {
            @page {
              size: A4;
              margin: 0;
            }
            body {
              margin: 20mm;
            }
          }
        </style>
      </head>
      <body>
        ${htmlContent}
      </body>
    </html>`;

  printFrame.onload = () => {
    printFrame.contentWindow?.print();
    setTimeout(() => {
      document.body.removeChild(printFrame);
    }, 1000);
  };

  document.body.appendChild(printFrame);
};

export const printDocument = ({
  markdownContent,
  htmlContent: prerenderedHtml,
  sessionId = '',
  documentId = '',
  onPrintStart,
}: PrintDocumentOptions): void => {
  if (onPrintStart) {
    onPrintStart({ sessionId, documentId });
  }

  const htmlContent =
    prerenderedHtml ??
    simpleMarkdownToHtmlForPrint({
      markdown: markdownContent,
    }) ??
    '';

  // The one point every print path's final HTML passes through — prerendered,
  // fallback-produced, or converted here — so the print surface scans at this
  // altitude rather than in any upstream builder.
  reportSectionMarkerResidue({
    payloads: [{ flavor: 'html', value: htmlContent }],
    surface: 'print',
    sessionId,
  });

  // A blank page is indistinguishable from a print that never fired, and this
  // is the only signal for callers that reach printDocument directly.
  if (!htmlContent) {
    logger.warn('[PRINT] printing empty content', {
      session_id: sessionId,
      document_id: documentId,
      markdown_length: markdownContent.length,
    });
  }

  if (isTauri()) {
    const store = getDefaultStore();
    const wasTutorialVisible = store.get(tutorialVisibleAtom);

    store.set(tutorialVisibleAtom, false);

    const printOverlay = document.createElement('div');
    printOverlay.id = 'tauri-print-overlay';

    const printContent = document.createElement('div');
    printContent.id = 'tauri-print-content';
    printContent.innerHTML = htmlContent;

    const closeButton = document.createElement('button');
    closeButton.innerHTML = '✕ Close';
    closeButton.style.cssText = `
      position: fixed;
      top: 40px;
      right: 20px;
      z-index: 10002;
      background: #333;
      color: white;
      border: none;
      padding: 10px 15px;
      border-radius: 5px;
      cursor: pointer;
      font-size: 14px;
      box-shadow: 0 2px 10px rgba(0,0,0,0.3);
    `;

    // Create print button
    const printButton = document.createElement('button');
    printButton.innerHTML = '🖨️ Print';
    printButton.style.cssText = `
      position: fixed;
      top: 40px;
      right: 120px;
      z-index: 10002;
      background: #007bff;
      color: white;
      border: none;
      padding: 10px 15px;
      border-radius: 5px;
      cursor: pointer;
      font-size: 14px;
      box-shadow: 0 2px 10px rgba(0,0,0,0.3);
    `;

    // Style the overlay
    printOverlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      background: white;
      z-index: 10000;
      overflow: auto;
      padding: 60px 20px 20px 20px;
      box-sizing: border-box;
    `;

    printContent.style.cssText = `
      max-width: 800px;
      margin: 0 auto;
      background: white;
      padding: 10mm;
      border: 1px solid #ddd;
      box-shadow: 0 0 10px rgba(0,0,0,0.1);
      font-family: ${DOCUMENT_FONT_STACK};
      font-size: 16px;
      line-height: 1.3;
      color: #222;
      min-height: calc(100vh - 120px);
    `;

    const overlayStyles = document.createElement('style');
    overlayStyles.id = 'tauri-overlay-styles';
    overlayStyles.textContent = `
      /* Headings - match DOCUMENT_BASE_CSS (Export/Print) at the default
         16px body: h1 18px, content headings h2-h5 26/22/18/16px. */
      #tauri-print-content h1 {
        font-size: 18px !important;
        font-weight: 500 !important;
      }

      #tauri-print-content h2 { font-size: 26px !important; }
      #tauri-print-content h3 { font-size: 22px !important; }
      #tauri-print-content h4 { font-size: 18px !important; }
      #tauri-print-content h5 { font-size: 16px !important; }

      #tauri-print-content h2,
      #tauri-print-content h3,
      #tauri-print-content h4 {
        line-height: 1.25 !important;
        letter-spacing: -0.025em !important;
      }

      #tauri-print-content h2,
      #tauri-print-content h3,
      #tauri-print-content h4,
      #tauri-print-content h5 {
        font-weight: 500 !important;
        margin: 0 0 12px 0 !important;
      }

      /* Paragraphs - match iframe styling */
      #tauri-print-content p.para {
        margin: 0 0 12px 0 !important;
        padding: 0 !important;
        display: block !important;
      }
      
      #tauri-print-content p {
        margin: 0 0 12px 0 !important;
        padding: 0 !important;
        display: block !important;
      }
      
      /* Lists - match iframe styling */
      #tauri-print-content ul.list {
        margin: 0 0 12px 20px !important;
        padding: 0 !important;
        list-style-type: disc !important;
        display: block !important;
      }
      
      #tauri-print-content ul {
        margin: 0 0 12px 20px !important;
        padding: 0 !important;
        list-style-type: disc !important;
        display: block !important;
      }

      /* Ordered lists from copy-settings-transformed editor HTML. */
      #tauri-print-content ol {
        margin: 0 0 12px 20px !important;
        padding: 0 !important;
        list-style-type: decimal !important;
        display: block !important;
      }

      #tauri-print-content ul.list li,
      #tauri-print-content ul li {
        margin: 0 0 10px 0 !important;
        padding: 0 !important;
        list-style-type: disc !important;
        display: list-item !important;
        margin-left: 0 !important;
        padding-left: 0 !important;
      }

      #tauri-print-content ol li {
        margin: 0 0 10px 0 !important;
        padding: 0 !important;
        list-style-type: decimal !important;
        display: list-item !important;
        margin-left: 0 !important;
        padding-left: 0 !important;
      }
      
      /* Text formatting */
      #tauri-print-content strong { 
        font-weight: 700 !important; 
      }
      
      #tauri-print-content em { 
        font-style: italic !important; 
      }
      
      #tauri-print-content hr {
        border: 0 !important;
        border-top: 1px solid #bbb !important;
        margin: 10px 0 !important;
        display: block !important;
      }
      
      #tauri-print-content br {
        display: block !important;
        margin: 0 0 10px 0 !important;
        content: " " !important;
        line-height: 1.2 !important;
      }

      /* Tables. Cell borders, shaded header row, full-width columns —
         borrowed from the exported PDF per the agreed output. */
      #tauri-print-content table {
        border-collapse: collapse !important;
        width: 100% !important;
        margin: 1em 0 !important;
      }
      #tauri-print-content table,
      #tauri-print-content th,
      #tauri-print-content td { border: 1px solid #ddd !important; }
      #tauri-print-content th,
      #tauri-print-content td { padding: 8px !important; text-align: left !important; }
      #tauri-print-content th { background-color: #F2F2F2 !important; font-weight: bold !important; }

      @media print {
        /* Break out of fixed positioning so content flows across pages */
        #tauri-print-overlay {
          position: static !important;
          width: auto !important;
          height: auto !important;
          overflow: visible !important;
          padding: 0 !important;
          box-shadow: none !important;
          border: none !important;
        }

        #tauri-print-content {
          max-width: none !important;
          margin: 0 !important;
          padding: 20mm !important;
          border: none !important;
          box-shadow: none !important;
          min-height: auto !important;
        }

        /* Hide everything except the print overlay */
        body > *:not(#tauri-print-overlay) {
          display: none !important;
        }

        /* Hide buttons during print */
        button {
          display: none !important;
        }

        @page {
          size: A4;
          margin: 0;
        }
      }
    `;

    const removeOverlay = () => {
      try {
        if (printOverlay.parentNode) {
          document.body.removeChild(printOverlay);
        }
        if (overlayStyles.parentNode) {
          document.head.removeChild(overlayStyles);
        }
        if (wasTutorialVisible) {
          store.set(tutorialVisibleAtom, true);
        }
      } catch (error) {
        // A stuck overlay makes the desktop app look frozen; log only the error
        // type so the failure is queryable without exporting note content.
        logger.warn('[PRINT] overlay teardown failed', {
          session_id: sessionId,
          document_id: documentId,
          error_name: error instanceof Error ? error.name : 'unknown',
        });
      }
    };
    closeButton.addEventListener('click', removeOverlay);
    printButton.addEventListener('click', () => {
      window.print();
    });
    printOverlay.addEventListener('click', (e) => {
      if (e.target === printOverlay) {
        removeOverlay();
      }
    });
    const escapeHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        removeOverlay();
        document.removeEventListener('keydown', escapeHandler);
      }
    };
    document.addEventListener('keydown', escapeHandler);
    printOverlay.appendChild(printContent);
    printOverlay.appendChild(closeButton);
    printOverlay.appendChild(printButton);
    document.head.appendChild(overlayStyles);
    document.body.appendChild(printOverlay);
    printButton.focus();
    setTimeout(() => {
      window.print();
    }, 300);
  } else {
    createPrintFrame(htmlContent);
  }
};
